import { Avatar, Tooltip } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CommentText from "./CommentText";
import { useState } from "react";
import { calTime } from "../helpers";
import { Link, useNavigate } from "react-router-dom";
import { REPORT_FORM } from "@constants/api";
import { useSelector } from "react-redux";
import OpenGraphInfo from "@components/OpenGraphInfo";

function CommentOptions({ children, onClick }) {
  return (
    <span
      onClick={onClick}
      style={{ fontSize: "12px" }}
      className="font-normal cursor-pointer mr-1 text-gray-500"
    >
      {children}
    </span>
  );
}

// Safely parse postLinkMetadata — backend may return it as a JSON string
function parseOgMetadata(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

// Self-contained OG preview — each comment/reply has its own independent
// hide/show state using only local useState. No external refs or dispatch needed.
function OgPreview({ metadata, initialHidden }) {
  const [hidden, setHidden] = useState(Boolean(initialHidden));
  const parsed = parseOgMetadata(metadata);

  if (!parsed) return null;

  // if (hidden) {
  //   return (
  //     <div className="flex justify-end my-2">
  //       <button
  //         type="button"
  //         onClick={() => setHidden(false)}
  //         style={{
  //           backgroundColor: '#51CDA0',
  //           color: '#fff',
  //           border: 'none',
  //           borderRadius: '20px',
  //           padding: '6px 16px',
  //           fontSize: '0.875rem',
  //           fontWeight: 500,
  //           cursor: 'pointer',
  //           textTransform: 'uppercase',
  //           letterSpacing: '0.02857em',
  //         }}
  //       >
  //         Show link preview
  //       </button>
  //     </div>
  //   );
  // }

  return (
    <div className="relative my-2">
      <OpenGraphInfo data={parsed} />
      {/* <Tooltip title="Remove link preview">
        <CloseIcon
          className="cursor-pointer absolute -top-2 -right-2"
          onClick={() => setHidden(true)}
        />
      </Tooltip> */}
    </div>
  );
}

export default function Comment({
  readOnly = false,
  comment,
  post,
  setCommentToReply,
  setCommentToEdit,
  setComment,
  onDelete,
  setPrevFile,
  overwritable,
  overwriteLevel,
  setCommentToOverwrite,
}) {
  const [showReply, setShowReply] = useState(true);
  const navigate = useNavigate();
  const { userRecord } = useSelector((state) => state.user);

  const onCommentEdit = (c) => {
    setCommentToEdit(c);
    setComment(c?.comment);
    setPrevFile(c?.media?.currBucketUrl);
  };

  const onCommentOverwrite = (c) => {
    setCommentToOverwrite(c);
    setComment(c?.comment);
    setPrevFile(c?.media?.currBucketUrl);
  };

  const isGroupPost = post?.groups?.length >= 1;
  const userIsPostGroupAdmin =
    isGroupPost && post.groups.some((group) => group.admin === userRecord.id);

  const commentUserToShow = comment?.overwrittenBy ?? comment?.user;

  // Parse stored postLinkMetadata which may be a JSON string from the DB
  const parsedMainOg = parseOgMetadata(comment.postLinkMetadata);

  return (
    <div className="flex flex-col">
      <div className="flex">
        <Link to={`/${commentUserToShow?.userName}`}>
          <Avatar
            src={commentUserToShow?.media?.currBucketUrl}
            className="z-0 mr-2 mt-3 self-start"
          />
        </Link>
        <div className="flex flex-col grow mr-12">
          {/* ── OG link preview for this comment ── */}
          {!comment.hideLinkPreview && parsedMainOg && (
            <div className="mt-1 mb-1">
              <OpenGraphInfo data={parsedMainOg} />
            </div>
          )}
          <div className="flex items-center">
            <CommentText
              readOnly={readOnly}
              user={commentUserToShow}
              media={comment.media}
              time={calTime(comment?.createdAt)}
              onEdit={() => onCommentEdit(comment)}
              overwrittenBy={comment?.overwrittenBy}
            >
              {comment.comment}
            </CommentText>
          </div>

          {/* ── OG link preview — self-contained dismiss/show state ── */}
          {/* <OgPreview
            metadata={comment.postLinkMetadata}
            initialHidden={Boolean(comment.hideLinkPreview)}
          /> */}

          {readOnly && <div className="mb-6" />}

          <div className={readOnly ? "hidden" : ""}>
            {overwritable && (
              <>
                <CommentOptions onClick={() => onCommentOverwrite(comment)}>
                  Overwrite
                </CommentOptions>
                <CommentOptions>|</CommentOptions>
              </>
            )}

            <CommentOptions
              onClick={() => {
                setComment(
                  `@[${commentUserToShow?.userName}](${commentUserToShow?.userName}) `,
                );
                setCommentToReply(comment);
              }}
            >
              Reply
            </CommentOptions>

            {userRecord?.id === commentUserToShow?.id && (
              <CommentOptions>|</CommentOptions>
            )}
            {userRecord?.id === commentUserToShow?.id && (
              <CommentOptions onClick={() => onDelete(comment?.id)}>
                Delete
              </CommentOptions>
            )}

            <CommentOptions>|</CommentOptions>
            <CommentOptions
              onClick={() =>
                navigate(REPORT_FORM, {
                  state: { id: comment?.id, report: "comment" },
                })
              }
            >
              Report
            </CommentOptions>
          </div>
        </div>
      </div>

      {/* ── Replies ── */}
      {showReply &&
        comment?.replies?.map((reply, key) => {
          const replyUserToShow = reply?.overwrittenBy ?? reply?.user;
          const replyOverwritable =
            userIsPostGroupAdmin &&
            reply.user.id !== userRecord.id &&
            reply.overwrittenBy?.id !== userRecord.id;

          return (
            <div key={key} className="ml-10 mt-2 flex">
              <Avatar
                src={replyUserToShow?.media?.currBucketUrl}
                className="z-0 mr-2 mt-3"
              />
              <div className="flex flex-col grow mr-6">
                {/* ── OG link preview for reply — self-contained ── */}
                <OgPreview
                  metadata={reply.postLinkMetadata}
                  initialHidden={Boolean(reply.hideLinkPreview)}
                />
                <div className="flex items-center">
                  <CommentText
                    media={reply.media}
                    user={replyUserToShow}
                    time={calTime(reply?.createdAt)}
                    onEdit={() => onCommentEdit(reply)}
                    overwrittenBy={reply?.overwrittenBy}
                  >
                    {reply?.comment}
                  </CommentText>
                </div>

                <div>
                  {replyOverwritable && (
                    <>
                      <CommentOptions onClick={() => onCommentOverwrite(reply)}>
                        Overwrite
                      </CommentOptions>
                      <CommentOptions>|</CommentOptions>
                    </>
                  )}

                  <CommentOptions
                    onClick={() => {
                      setComment(`@${replyUserToShow?.userName} `);
                      setCommentToReply(comment);
                    }}
                  >
                    Reply
                  </CommentOptions>

                  {userRecord?.id === replyUserToShow?.id && (
                    <CommentOptions>|</CommentOptions>
                  )}
                  {userRecord?.id === replyUserToShow?.id && (
                    <CommentOptions onClick={() => onDelete(reply?.id)}>
                      Delete
                    </CommentOptions>
                  )}

                  <CommentOptions>|</CommentOptions>
                  <CommentOptions
                    onClick={() =>
                      navigate(REPORT_FORM, {
                        state: { id: comment?.id, report: "comment" },
                      })
                    }
                  >
                    Report
                  </CommentOptions>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}
